// AKSU Business School - Active Module Knowledge Checks & Interactive Flashcards
// Domain: businessschool.aksu.edu.ng
// Tailored assessments for all 15 Semester Courses across DBA, MAF, and DAF

export const COURSE_ASSESSMENTS = {
  // =======================================================================================
  // 1. BUS 901: Data Science and Artificial intelligence (Dr. Imoh Uford)
  // =======================================================================================
  "BUS 901": {
    quizTitle: "BUS 901: Enterprise Data Science & AI Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating enterprise machine learning, LLM governance, and predictive modeling competence.",
    flashcardTitle: "BUS 901 Core AI & Data Science Flashcards",
    flashcardSubtitle: "Key architectural concepts in enterprise artificial intelligence and algorithmic strategy.",
    questions: [
      {
        id: "bus901-q1",
        question: "In formulating an enterprise predictive analytics roadmap under volatile emerging market conditions, what is the primary operational advantage of deploying ensemble methods (e.g., XGBoost, Random Forests) over conventional Ordinary Least Squares (OLS)?",
        options: [
          { key: "A", text: "Captures complex non-linear feature interactions and mitigates overfitting through bagging and boosting regularization" },
          { key: "B", text: "Guarantees complete elimination of observational sampling error" },
          { key: "C", text: "Removes the requirement for data cleaning and outlier detection" },
          { key: "D", text: "Automatically proves economic causation without theoretical grounding" }
        ],
        correctKey: "A",
        explanation: "Ensemble architectures like XGBoost and Random Forests leverage decision trees with regularization to capture high-dimensional non-linear relationships without succumbing to severe overfitting."
      },
      {
        id: "bus901-q2",
        question: "Under the Nigeria Data Protection Act (NDPA) and NDPR frameworks, what institutional architecture must a C-suite executive mandate before integrating proprietary corporate knowledge bases with third-party Large Language Model (LLM) APIs?",
        options: [
          { key: "A", text: "Mandatory public release of all underlying corporate training weights" },
          { key: "B", text: "Retrieval-Augmented Generation (RAG) with local tokenization, vector data encryption, and strict processing boundary agreements" },
          { key: "C", text: "Complete manual verification of every generated prompt by the company secretary" },
          { key: "D", text: "Elimination of vector embeddings and relational databases" }
        ],
        correctKey: "B",
        explanation: "RAG architectures query private encrypted vector databases locally, ensuring proprietary corporate records are never absorbed into public LLM training corpuses while enforcing NDPA compliance."
      },
      {
        id: "bus901-q3",
        question: "When deploying unsupervised k-means clustering to segment commercial clients for algorithmic credit risk scoring, which diagnostic metric determines the mathematically optimal number of clusters?",
        options: [
          { key: "A", text: "Adjusted R-squared" },
          { key: "B", text: "Silhouette Coefficient Analysis alongside the Elbow Method of Within-Cluster Sum of Squares (WCSS)" },
          { key: "C", text: "Durbin-Watson statistic" },
          { key: "D", text: "Student's t-distribution critical values" }
        ],
        correctKey: "B",
        explanation: "The Elbow Method identifies where diminishing returns occur in explained variance, while Silhouette Coefficients measure how well-separated each cluster is from its nearest neighbor."
      }
    ],
    flashcards: [
      {
        term: "Retrieval-Augmented Generation (RAG)",
        question: "What is RAG in Enterprise Corporate AI?",
        definition: "An architecture that retrieves authoritative domain documents from a private corporate vector store and injects them into an LLM prompt to ground model outputs in validated enterprise facts.",
        context: "Prevents LLM hallucinations during board policy querying and regulatory compliance audits."
      },
      {
        term: "Gradient Boosting (XGBoost)",
        question: "Why is XGBoost dominant in tabular business analytics?",
        definition: "An ensemble learning technique that sequentially trains weak decision trees, where each successive tree minimizes residual loss gradients with L1/L2 regularization to prevent overfitting.",
        context: "The gold standard for corporate credit scoring, churn prediction, and supply chain logistics modeling."
      },
      {
        term: "Algorithmic Bias & Fair AI",
        question: "How does algorithmic bias arise in automated corporate decision-making?",
        definition: "Systematic inaccuracies that favor or disadvantage particular demographic groups, stemming from unrepresentative historical training data or socio-economic proxy variables.",
        context: "A significant institutional liability under NDPA and international board governance frameworks."
      },
      {
        term: "Feature Engineering",
        question: "What is the strategic role of Feature Engineering in executive analytics?",
        definition: "The domain-driven process of transforming raw transactional or sensor data into meaningful mathematical predictors that improve machine learning model performance.",
        context: "Converts millions of raw ERP logs into predictive executive risk metrics."
      }
    ]
  },

  // =======================================================================================
  // 2. BUS 911: Adv. Quant. Analysis & Econometrics (Dr Ekere Daniel)
  // =======================================================================================
  "BUS 911": {
    quizTitle: "BUS 911: Advanced Econometrics & Quantitative Analysis Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating dynamic panel data, GMM, time-series cointegration, and PLS-SEM proficiency.",
    flashcardTitle: "BUS 911 Econometric Foundations Flashcards",
    flashcardSubtitle: "Core econometric principles in panel estimation, stationarity, and structural modeling.",
    questions: [
      {
        id: "bus911-q1",
        question: "When estimating dynamic panel models where lagged dependent variables correlate with unobserved individual firm fixed effects, which estimator is econometrically necessary to eliminate Nickell bias?",
        options: [
          { key: "A", text: "Pooled Ordinary Least Squares (OLS)" },
          { key: "B", text: "Arellano-Bond Generalized Method of Moments (Difference or System GMM)" },
          { key: "C", text: "Classical Random Effects Generalized Least Squares (GLS)" },
          { key: "D", text: "Unweighted cross-sectional correlation" }
        ],
        correctKey: "B",
        explanation: "In dynamic panel models with $T$ small and $N$ large, lagged dependent variables create correlation with the fixed effects error component (Nickell bias). System GMM uses internal lagged instruments to yield consistent parameters."
      },
      {
        id: "bus911-q2",
        question: "In time-series cointegration analysis, what does the rejection of the null hypothesis of zero cointegrating vectors in a Johansen Test demonstrate?",
        options: [
          { key: "A", text: "The variables must be differenced indefinitely because they lack any mathematical linkage" },
          { key: "B", text: "There exists a true long-run stationary equilibrium relationship preventing the non-stationary series from drifting apart" },
          { key: "C", text: "The model suffers from severe multicollinearity and must be discarded" },
          { key: "D", text: "The residuals are guaranteed to follow a uniform probability distribution" }
        ],
        correctKey: "B",
        explanation: "Cointegration confirms that despite individual non-stationarity I(1), a linear combination of the series is stationary I(0), demonstrating an authentic long-run economic equilibrium."
      },
      {
        id: "bus911-q3",
        question: "In Partial Least Squares Structural Equation Modeling (PLS-SEM), which diagnostic criteria establish adequate convergent validity of a reflective latent construct?",
        options: [
          { key: "A", text: "Average Variance Extracted (AVE) >= 0.50 and Composite Reliability (rho_c) >= 0.70" },
          { key: "B", text: "P-value strictly greater than 0.50 and Chi-square = 0" },
          { key: "C", text: "Variance Inflation Factor (VIF) > 10.0" },
          { key: "D", text: "Durbin-Watson statistic strictly below 1.0" }
        ],
        correctKey: "A",
        explanation: "An AVE of at least 0.50 confirms that the construct explains more than half of its indicator variance, while Composite Reliability above 0.70 confirms acceptable internal consistency."
      }
    ],
    flashcards: [
      {
        term: "System GMM (Blundell & Bond)",
        question: "Why is System GMM essential in corporate financial econometrics?",
        definition: "An estimator combining equations in differences with equations in levels, using lagged differences as instruments for level equations to resolve endogeneity and weak instruments.",
        context: "The gold standard for capital structure, firm leverage, and corporate governance regressions."
      },
      {
        term: "Johansen Cointegration Test",
        question: "What does the Johansen Cointegration test establish?",
        definition: "A maximum likelihood test for multiple cointegrating vectors in Vector Autoregression (VAR) systems, evaluating whether non-stationary series share common stochastic trends.",
        context: "Essential for examining long-run relationships between exchange rates, oil prices, and stock returns."
      },
      {
        term: "Heteroskedasticity (White Test)",
        question: "What occurs when regression error terms are heteroskedastic?",
        definition: "The variance of error terms is non-constant across observations. OLS estimators remain unbiased, but standard errors are biased, invalidating confidence intervals and t-statistics.",
        context: "Rectified in doctoral studies by computing Huber-White heteroskedasticity-consistent robust standard errors."
      },
      {
        term: "Endogeneity & 2SLS",
        question: "What creates econometric endogeneity in business models?",
        definition: "A violation of Gauss-Markov assumptions where an explanatory variable correlates with the error term due to omitted variables, measurement error, or reverse causality.",
        context: "Solved through instrumental variables (Two-Stage Least Squares) or exogenous policy shocks."
      }
    ]
  },

  // =======================================================================================
  // 3. BUS 921: Adv. Business Mgmt Theory (Prof. Christabel Brownson)
  // =======================================================================================
  "BUS 921": {
    quizTitle: "BUS 921: Advanced Business Management Theory Knowledge Check",
    quizSubtitle: "Continuous Assessment · Evaluating dynamic capabilities, institutional isomorphism, and theoretical paradigms.",
    flashcardTitle: "BUS 921 Epistemological Theory Flashcards",
    flashcardSubtitle: "Classical and contemporary theoretical constructs in corporate management inquiry.",
    questions: [
      {
        id: "bus921-q1",
        question: "According to David Teece's Dynamic Capabilities framework, what are the three foundational clusters of organizational processes necessary to sustain competitive advantage in volatile environments?",
        options: [
          { key: "A", text: "Downsizing, outsourcing, and short-term debt refinancing" },
          { key: "B", text: "Sensing (opportunities/threats), Seizing (mobilizing resources), and Transforming (continuous reconfiguration)" },
          { key: "C", text: "Standardized accounting, fixed budgeting, and rigid organizational hierarchies" },
          { key: "D", text: "Pure price cutting, patent litigation, and aggressive lobbying" }
        ],
        correctKey: "B",
        explanation: "Teece defines dynamic capabilities as the meta-competence of Sensing, Seizing, and Transforming assets to maintain alignment with turbulent market ecosystems."
      },
      {
        id: "bus921-q2",
        question: "Under Barney's Resource-Based View (RBV) and VRIO framework, when does a corporate resource generate a SUSTAINED competitive advantage?",
        options: [
          { key: "A", text: "When it is widely traded on open commodity spot markets" },
          { key: "B", text: "When it is Valuable, Rare, Inimitable (imperfectly copyable), and Organised for value capture" },
          { key: "C", text: "When it can be immediately acquired from overseas third-party vendors" },
          { key: "D", text: "When it has zero initial capital cost" }
        ],
        correctKey: "B",
        explanation: "Valuable and rare resources grant temporary advantage; sustained competitive advantage requires inimitability (path dependency, causal ambiguity) and organizational readiness."
      },
      {
        id: "bus921-q3",
        question: "In Institutional Theory, what mechanism describes organizations copying the business models of perceived industry leaders during times of intense macroeconomic ambiguity?",
        options: [
          { key: "A", text: "Coercive Isomorphism" },
          { key: "B", text: "Mimetic Isomorphism" },
          { key: "C", text: "Normative Isomorphism" },
          { key: "D", text: "Structural Functionalism" }
        ],
        correctKey: "B",
        explanation: "DiMaggio and Powell define Mimetic Isomorphism as the tendency of enterprises to benchmark and imitate successful peers when environmental uncertainty makes optimal paths ambiguous."
      }
    ],
    flashcards: [
      {
        term: "Dynamic Capabilities",
        question: "How does Teece define Dynamic Capabilities?",
        definition: "The firm’s ability to integrate, build, and reconfigure internal and external competences to address rapidly changing corporate environments.",
        context: "Explains how legacy conglomerates survive technological disruptions."
      },
      {
        term: "Barney's VRIO Framework",
        question: "What are the four pillars of VRIO?",
        definition: "Valuable (exploits opportunities), Rare (controlled by few), Inimitable (costly to copy/substitute), and Organized (internal structures positioned to exploit).",
        context: "The core analytical matrix for evaluating internal strategic assets in DBA theses."
      },
      {
        term: "Institutional Isomorphism",
        question: "What are the three drivers of Institutional Isomorphism?",
        definition: "1. Coercive (legal/governmental mandates), 2. Mimetic (imitation under uncertainty), and 3. Normative (professional standards and credentialing).",
        context: "Explains why financial institutions across Nigeria establish identical risk governance committees."
      },
      {
        term: "Agency Theory & Asymmetry",
        question: "What is the core conflict in Agency Theory?",
        definition: "Conflicts of interest arising between principals (shareholders) and agents (corporate executives) due to information asymmetry, divergent time horizons, and risk tolerance.",
        context: "Underpins board oversight committees, executive stock options, and independent audits."
      }
    ]
  },

  // =======================================================================================
  // 4. BUS 931: Competitive Strategy (Prof. Anietie Akpan)
  // =======================================================================================
  "BUS 931": {
    quizTitle: "BUS 931: Competitive Strategy & Industrial Organization Knowledge Check",
    quizSubtitle: "Continuous Assessment · Testing Porterian structural analysis, Blue Ocean innovation, and strategic moats.",
    flashcardTitle: "BUS 931 Corporate Strategy Flashcards",
    flashcardSubtitle: "Strategic frameworks for competitive advantage and high-stakes market positioning.",
    questions: [
      {
        id: "bus931-q1",
        question: "In Porter's Five Forces framework, which structural market condition significantly amplifies the bargaining power of buyers in an industrial corridor?",
        options: [
          { key: "A", text: "High supplier switching costs and bespoke product specifications" },
          { key: "B", text: "High buyer concentration where a few large corporate buyers account for the majority of the seller's total revenue" },
          { key: "C", text: "The complete absence of substitute products or viable alternatives" },
          { key: "D", text: "Heavy intellectual property barriers surrounding supplier manufacturing" }
        ],
        correctKey: "B",
        explanation: "When buyers are concentrated or purchase in massive volumes, they possess immense bargaining leverage to demand price concessions, custom terms, and enhanced warranties."
      },
      {
        id: "bus931-q2",
        question: "Under Chan Kim and Renée Mauborgne's Blue Ocean Strategy, what constitutes the central principle of 'Value Innovation'?",
        options: [
          { key: "A", text: "Engaging in relentless price wars against established incumbents in mature saturated sectors" },
          { key: "B", text: "Pursuing differentiation and low cost simultaneously to break the traditional cost-value trade-off and open uncontested market space" },
          { key: "C", text: "Purchasing small competitors to achieve monopoly status" },
          { key: "D", text: "Maximizing short-term executive bonuses through aggressive balance sheet leverage" }
        ],
        correctKey: "B",
        explanation: "Value Innovation rejects the conventional strategic trade-off between low cost and high differentiation by eliminating and reducing non-essential factors while raising and creating novel value curves."
      },
      {
        id: "bus931-q3",
        question: "What structural vulnerability confronts a company that attempts Cost Leadership and Differentiation concurrently without possessing distinct competencies in either?",
        options: [
          { key: "A", text: "Disintermediation" },
          { key: "B", text: "Becoming 'Stuck in the Middle' with substandard margins and ambiguous customer positioning" },
          { key: "C", text: "Horizontal Cannibalization" },
          { key: "D", text: "Excessive economies of scale" }
        ],
        correctKey: "B",
        explanation: "Porter emphasizes that firms stuck in the middle fail to cultivate the cost advantage of low-cost leaders or the price premium of differentiated competitors."
      }
    ],
    flashcards: [
      {
        term: "Porter's Five Forces",
        question: "What are Porter's Five Structural Forces?",
        definition: "1. Threat of New Entrants, 2. Bargaining Power of Buyers, 3. Bargaining Power of Suppliers, 4. Threat of Substitute Products, 5. Rivalry Among Existing Competitors.",
        context: "Determines the fundamental economic attractiveness and profitability ceiling of an industry."
      },
      {
        term: "Blue Ocean vs Red Ocean",
        question: "What is the core distinction in market space?",
        definition: "Red Oceans represent crowded industries with defined boundaries where rivals fight for market share. Blue Oceans create uncontested market space by rendering competition irrelevant.",
        context: "Applied to maritime logistics and integrated deep-sea terminal operations in West Africa."
      },
      {
        term: "Switching Costs & Moats",
        question: "How do switching costs protect corporate margins?",
        definition: "The financial, operational, or psychological friction a customer incurs to switch from an incumbent to a competitor. High switching costs prevent customer attrition.",
        context: "A primary defensive moat in enterprise ERP software and downstream energy logistics."
      },
      {
        term: "Strategic Group Mapping",
        question: "What is the purpose of Strategic Group Mapping?",
        definition: "An analytical technique for displaying rival firms in an industry along two non-correlated strategic dimensions to identify cluster direct rivals and market opportunities.",
        context: "Reveals mobility barriers separating high-margin premium players from volume operators."
      }
    ]
  },

  // =======================================================================================
  // 5. BUS 941 & DAF 941: Business writing, publication & research ethics (Dr Eno Ukpong)
  // =======================================================================================
  "BUS 941": {
    quizTitle: "BUS 941: Doctoral Business Writing & Publication Ethics Knowledge Check",
    quizSubtitle: "Continuous Assessment · Evaluating scholarly manuscript structure, peer review navigation, and publication ethics.",
    flashcardTitle: "BUS 941 Doctoral Publishing Flashcards",
    flashcardSubtitle: "Standards for publishing in high-impact ABS and Scopus indexed business journals.",
    questions: [
      {
        id: "bus941-q1",
        question: "According to the Committee on Publication Ethics (COPE) and AKSU Senate standards, what is the ethical violation when an author submits identical empirical findings to two journals simultaneously?",
        options: [
          { key: "A", text: "Fair academic dissemination" },
          { key: "B", text: "Duplicate / Simultaneous Submission and Self-Plagiarism" },
          { key: "C", text: "Constructive theoretical triangulation" },
          { key: "D", text: "Permissible preprint syndication" }
        ],
        correctKey: "B",
        explanation: "Submitting the same paper or essentially identical empirical findings to multiple journals simultaneously wastes editorial peer review resources and violates core COPE publishing ethics."
      },
      {
        id: "bus941-q2",
        question: "In structuring an executive doctoral paper targeting ABS 3* or 4* journals, what must the Introduction section establish prior to explaining methodology?",
        options: [
          { key: "A", text: "The entire spreadsheet of raw survey responses" },
          { key: "B", text: "The theoretical gap, empirical puzzle, and explicit novel contribution to management literature" },
          { key: "C", text: "A detailed autobiographical history of the doctoral candidate" },
          { key: "D", text: "A list of commercial consulting clients" }
        ],
        correctKey: "B",
        explanation: "Top-tier journal reviewers immediately assess whether the manuscript articulates a compelling theoretical tension, an unaddressed empirical puzzle, and an explicit contribution to theory."
      },
      {
        id: "bus941-q3",
        question: "When responding to critical journal reviewer feedback during a Revise & Resubmit (R&R) cycle, what is the required professional format?",
        options: [
          { key: "A", text: "Submit an amended manuscript without addressing specific reviewer critiques" },
          { key: "B", text: "A comprehensive point-by-point Rebuttal Matrix documenting exact reviewer comments, author responses, and corresponding page/line revisions" },
          { key: "C", text: "Request the immediate reassignment of reviewers to an alternate editorial team" },
          { key: "D", text: "Withdraw the paper and publish in an unindexed predatory journal" }
        ],
        correctKey: "B",
        explanation: "A structured Rebuttal Matrix provides polite, scholarly, point-by-point responses, demonstrating that every reviewer concern was rigorously evaluated and integrated into the revised text."
      }
    ],
    flashcards: [
      {
        term: "COPE Standards",
        question: "What is the mandate of COPE in postgraduate research?",
        definition: "Committee on Publication Ethics: international guidelines establishing ethical codes for authorship attribution, conflict of interest disclosures, and research integrity.",
        context: "The ethical benchmark enforced across AKSU doctoral dissertations."
      },
      {
        term: "Theoretical Contribution",
        question: "What constitutes a genuine Theoretical Contribution?",
        definition: "Advancing scholarly conversation by challenging, refining, extending, or integrating existing theoretical models to explain organizational phenomena previously unaccounted for.",
        context: "The mandatory prerequisite for acceptance in ABS 3* / 4* ranked journals."
      },
      {
        term: "Rebuttal Matrix",
        question: "What is the function of a Journal Rebuttal Matrix?",
        definition: "A tabular response dossier systematically mapping each referee comment to the author’s response and the exact page, paragraph, and line changes made in the revised manuscript.",
        context: "The primary vehicle for turning a Revise & Resubmit (R&R) into an unconditional acceptance."
      },
      {
        term: "Turnitin / iThenticate Similarity",
        question: "How does similarity index differ from plagiarism?",
        definition: "Similarity index measures text matching percentage; plagiarism represents using others' ideas, words, or structures without attribution. Low similarity can still harbor plagiarism if uncited.",
        context: "AKSU Senate mandates a strict similarity threshold of under 15% for all postgraduate theses."
      }
    ]
  },

  // =======================================================================================
  // 6. MAF 801: Bus. Stats & Data Analytics (Dr Imoh Uford)
  // =======================================================================================
  "MAF 801": {
    quizTitle: "MAF 801: Business Statistics & Financial Analytics Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating probability distributions, hypothesis testing, and financial regression analytics.",
    flashcardTitle: "MAF 801 Financial Statistics Flashcards",
    flashcardSubtitle: "Core statistical techniques for financial analytics and empirical decision modeling.",
    questions: [
      {
        id: "maf801-q1",
        question: "When testing whether an equity fund manager generates statistically significant alpha (abnormal returns) above the market benchmark, which hypothesis test is appropriate for a single sample mean?",
        options: [
          { key: "A", text: "One-sample Student's t-test comparing the sample mean return against zero" },
          { key: "B", text: "Pearson Chi-square test of contingency tables" },
          { key: "C", text: "Levene's test of equality of variances" },
          { key: "D", text: "Kruskal-Wallis non-parametric rank sum test" }
        ],
        correctKey: "A",
        explanation: "A one-sample t-test determines whether the mean excess return (alpha) is statistically significantly different from zero, accounting for sample variance and sample size."
      },
      {
        id: "maf801-q2",
        question: "In multiple regression modeling for corporate bankruptcy prediction, what does a Variance Inflation Factor (VIF > 5.0) between explanatory variables indicate?",
        options: [
          { key: "A", text: "Zero autocorrelation among regression residuals" },
          { key: "B", text: "Substantial Multicollinearity that inflates standard errors and renders coefficient t-tests unreliable" },
          { key: "C", text: "Perfect normal distribution of error terms" },
          { key: "D", text: "Absence of omitted variable bias" }
        ],
        correctKey: "B",
        explanation: "VIF measures how much the variance of an estimated regression coefficient increases when predictors are correlated. High VIFs (> 5 or 10) indicate collinearity requiring dimensionality reduction."
      },
      {
        id: "maf801-q3",
        question: "In financial time-series modeling, what does a Durbin-Watson statistic significantly lower than 2.0 (e.g., 0.85) signal?",
        options: [
          { key: "A", text: "Positive first-order autocorrelation in regression residuals" },
          { key: "B", text: "Perfect absence of any serial dependency" },
          { key: "C", text: "Negative first-order serial correlation" },
          { key: "D", text: "Optimal ordinary least squares specification" }
        ],
        correctKey: "A",
        explanation: "A Durbin-Watson value near 2.0 indicates no serial correlation. Values moving towards 0 denote positive first-order autocorrelation, requiring Cochrane-Orcutt or Newey-West standard errors."
      }
    ],
    flashcards: [
      {
        term: "Multicollinearity & VIF",
        question: "What is Variance Inflation Factor in financial modeling?",
        definition: "A metric quantifying the degree of multicollinearity in an OLS regression. VIF values greater than 5 or 10 indicate problematic correlation between financial predictors.",
        context: "Diagnosed when asset turnover, operating margin, and sales growth exhibit high mutual correlation."
      },
      {
        term: "Student's t-Distribution",
        question: "When is the t-distribution used over the z-distribution?",
        definition: "When analyzing small or moderate sample sizes ($n < 30$) or when the population standard deviation ($\sigma$) is unknown and must be estimated from sample variance ($s$).",
        context: "The baseline test distribution for evaluating regression coefficients in corporate finance."
      },
      {
        term: "P-Value & Significance",
        question: "What does the p-value signify in financial research?",
        definition: "The probability of observing sample statistics as extreme as those measured, assuming the null hypothesis is true. A p-value < 0.05 rejects the null at the 5% significance level.",
        context: "Determines whether corporate governance metrics genuinely influence firm performance."
      },
      {
        term: "ANOVA (F-Test)",
        question: "What does the ANOVA F-test evaluate in multiple regression?",
        definition: "Tests the global null hypothesis that all slope coefficients are simultaneously equal to zero, confirming whether the model explains variance beyond random chance.",
        context: "The initial hurdle test before interpreting individual financial beta coefficients."
      }
    ]
  },

  // =======================================================================================
  // 7. ACC 801: Fin.Reporting under IFRS (Dr Mbobo Mbobo)
  // =======================================================================================
  "ACC 801": {
    quizTitle: "ACC 801: Financial Reporting Under IFRS Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating IFRS 9 financial instruments, IFRS 15 revenue, and IFRS 16 lease accounting.",
    flashcardTitle: "ACC 801 IFRS Financial Reporting Flashcards",
    flashcardSubtitle: "Authoritative International Financial Reporting Standards and consolidated reporting principles.",
    questions: [
      {
        id: "acc801-q1",
        question: "Under IFRS 9 (Financial Instruments), how must a commercial bank classify an investment in corporate bonds if the business model objective is both to collect contractual cash flows and sell, and the cash flows meet the SPPI test?",
        options: [
          { key: "A", text: "Amortised Cost" },
          { key: "B", text: "Fair Value Through Other Comprehensive Income (FVOCI)" },
          { key: "C", text: "Fair Value Through Profit or Loss (FVTPL)" },
          { key: "D", text: "Historical Replacement Value" }
        ],
        correctKey: "B",
        explanation: "Debt instruments held under a 'Hold to Collect and Sell' business model passing the Solely Payments of Principal and Interest (SPPI) test are classified as FVOCI."
      },
      {
        id: "acc801-q2",
        question: "Under IFRS 16 (Leases), how must a corporate lessee recognize a 10-year lease for warehouse facilities on its Statement of Financial Position?",
        options: [
          { key: "A", text: "Record annual rental payments as off-balance sheet operating expenses in the notes" },
          { key: "B", text: "Recognize a Right-of-Use (ROU) Asset and a corresponding Lease Liability at the present value of lease payments" },
          { key: "C", text: "Charge the full undiscounted 10-year lease sum directly against opening retained earnings" },
          { key: "D", text: "Record the lease strictly as an intangible goodwill asset" }
        ],
        correctKey: "B",
        explanation: "IFRS 16 eliminated the distinction between operating and finance leases for lessees, requiring balance sheet recognition of a Right-of-Use (ROU) asset and interest-bearing lease liability."
      },
      {
        id: "acc801-q3",
        question: "Under IFRS 15 (Revenue from Contracts with Customers), what constitutes Step 2 of the mandatory five-step revenue recognition framework?",
        options: [
          { key: "A", text: "Determine the final transaction price" },
          { key: "B", text: "Identify the distinct performance obligations promised in the commercial contract" },
          { key: "C", text: "Allocate variable consideration across milestones" },
          { key: "D", text: "Collect cash remittance via banking channels" }
        ],
        correctKey: "B",
        explanation: "Step 1: Identify contract; Step 2: Identify distinct performance obligations; Step 3: Determine transaction price; Step 4: Allocate price; Step 5: Recognize revenue as obligations are satisfied."
      }
    ],
    flashcards: [
      {
        term: "IFRS 9 Expected Credit Loss (ECL)",
        question: "What are the three stages of the IFRS 9 ECL model?",
        definition: "Stage 1: 12-month ECL for performing loans; Stage 2: Lifetime ECL for credit with significant increase in risk (SICR); Stage 3: Lifetime ECL for credit-impaired non-performing loans.",
        context: "Replaced the backward-looking IAS 39 incurred loss model, shifting banks to forward-looking provisioning."
      },
      {
        term: "IFRS 16 Right-of-Use (ROU) Asset",
        question: "What is an ROU Asset under IFRS 16?",
        definition: "An asset representing a lessee’s legal right to control the use of an identified physical asset over the lease term, measured at present value of lease payments plus initial direct costs.",
        context: "Brought billions of Naira in previously off-balance sheet commercial leases onto balance sheets."
      },
      {
        term: "Fair Value Hierarchy (IFRS 13)",
        question: "What are the three levels of fair value inputs?",
        definition: "Level 1: Quoted market prices in active markets; Level 2: Observable inputs other than Level 1 (e.g. yield curves); Level 3: Unobservable inputs based on internal corporate cash flow models.",
        context: "Crucial for assessing audit subjectivity and corporate valuation adjustments."
      },
      {
        term: "SPPI Test",
        question: "What does the SPPI test evaluate in debt classification?",
        definition: "Solely Payments of Principal and Interest: verifies whether contractual cash flows consist strictly of repayment of principal and time value of money/credit risk interest.",
        context: "A debt instrument failing SPPI must be classified as FVTPL regardless of business model."
      }
    ]
  },

  // =======================================================================================
  // 8. FIN 811 & FIN 911: Corp. Finance, Valuation & Asset Pricing (Prof. Anthonia Ubom)
  // =======================================================================================
  "FIN 811": {
    quizTitle: "FIN 811: Corporate Finance & Valuation Knowledge Check",
    quizSubtitle: "Continuous Assessment · Testing WACC estimation, DCF valuation models, and capital structure dynamics.",
    flashcardTitle: "FIN 811 Corporate Valuation Flashcards",
    flashcardSubtitle: "Valuation models, cost of capital, and corporate capital restructuring.",
    questions: [
      {
        id: "fin811-q1",
        question: "In calculating the Weighted Average Cost of Capital (WACC) for corporate valuation, how is the tax shield on corporate debt treated?",
        options: [
          { key: "A", text: "The cost of debt is multiplied by (1 - Corporate Tax Rate) because interest expense is tax-deductible" },
          { key: "B", text: "Debt interest is treated as an equity dividend and taxed twice" },
          { key: "C", text: "The tax shield is added directly to the equity beta coefficient" },
          { key: "D", text: "Tax rates are completely disregarded in discounted cash flow valuation" }
        ],
        correctKey: "A",
        explanation: "Because interest payments reduce taxable corporate earnings, the effective after-tax cost of debt is $K_d \\times (1 - t)$, lowering the overall discount rate."
      },
      {
        id: "fin811-q2",
        question: "Under the Capital Asset Pricing Model (CAPM), if the risk-free rate is 14%, the expected market return is 20%, and a firm’s equity Beta is 1.5, what is the cost of equity ($K_e$)?",
        options: [
          { key: "A", text: "17.0%" },
          { key: "B", text: "23.0%" },
          { key: "C", text: "30.0%" },
          { key: "D", text: "14.5%" }
        ],
        correctKey: "B",
        explanation: "$K_e = R_f + \\beta \\times (R_m - R_f) = 14\\% + 1.5 \\times (20\\% - 14\\%) = 14\\% + 1.5 \\times 6\\% = 14\\% + 9\\% = 23\\%$."
      },
      {
        id: "fin811-q3",
        question: "In Discounted Cash Flow (DCF) enterprise valuation, what component typically represents between 60% and 80% of total enterprise value for a going-concern company?",
        options: [
          { key: "A", text: "Year 1 Free Cash Flow" },
          { key: "B", text: "Terminal Value (perpetuity growth of normalized cash flows)" },
          { key: "C", text: "Short-term accounts receivable balance" },
          { key: "D", text: "Executive share options" }
        ],
        correctKey: "B",
        explanation: "Terminal value captures all free cash flows beyond the discrete explicit forecast period in perpetuity, routinely dominating overall DCF valuations."
      }
    ],
    flashcards: [
      {
        term: "WACC (Weighted Average Cost of Capital)",
        question: "What is WACC in enterprise valuation?",
        definition: "The weighted average of after-tax cost of debt and cost of equity, weighted by target market values: $WACC = (E/V)K_e + (D/V)K_d(1 - t)$.",
        context: "The fundamental hurdle rate for corporate investment appraisal and DCF discounting."
      },
      {
        term: "Free Cash Flow to Firm (FCFF)",
        question: "How is FCFF derived from operating profits?",
        definition: "$FCFF = EBIT(1 - t) + \\text{Depreciation} - \\text{CapEx} - \\Delta\\text{Net Working Capital}$.",
        context: "Represents cash generated from core operations available to all capital providers without financing distortions."
      },
      {
        term: "Gordon Growth Terminal Value",
        question: "How is perpetuity terminal value calculated?",
        definition: "$TV = [FCFF_{n} \\times (1 + g)] / (WACC - g)$, where $g$ is the perpetual sustainable economic growth rate.",
        context: "Highly sensitive parameter requiring rigorous macroeconomic justification."
      },
      {
        term: "Modigliani-Miller Proposition II",
        question: "How does financial leverage impact cost of equity?",
        definition: "With corporate taxes, adding debt increases the financial risk to equity holders, raising the required return on equity linearly: $K_e = K_u + (K_u - K_d)(1 - t)(D/E)$.",
        context: "Guides board deliberations on optimal corporate debt-equity mix."
      }
    ]
  },

  // =======================================================================================
  // 9. ACC 811: Mgmt Accounting & Control (Dr S A Okpo)
  // =======================================================================================
  "ACC 811": {
    quizTitle: "ACC 811: Management Accounting & Control Systems Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating Activity-Based Costing, Balanced Scorecard, and transfer pricing control.",
    flashcardTitle: "ACC 811 Strategic Cost Management Flashcards",
    flashcardSubtitle: "Managerial control, cost driver allocation, and performance measurement systems.",
    questions: [
      {
        id: "acc811-q1",
        question: "What is the primary distortion caused by traditional volume-based overhead allocation (e.g., machine hours) in automated multi-product enterprises?",
        options: [
          { key: "A", text: "Over-allocates overhead to high-volume simple products while under-costing low-volume complex customized products" },
          { key: "B", text: "Exaggerates direct material invoice costs" },
          { key: "C", text: "Violates corporate income tax filing deadlines" },
          { key: "D", text: "Forces sales managers to price below prime cost" }
        ],
        correctKey: "A",
        explanation: "Volume-based allocation assumes overhead scales with production units. In reality, batch setups, design changes, and QA inspections consume overhead regardless of batch size."
      },
      {
        id: "acc811-q2",
        question: "In the Kaplan-Norton Balanced Scorecard framework, which four integrated organizational perspectives prevent executive over-reliance on short-term financial metrics?",
        options: [
          { key: "A", text: "Financial, Customer, Internal Business Processes, and Learning & Growth" },
          { key: "B", text: "Marketing, Advertising, Sales Distribution, and After-Sales Maintenance" },
          { key: "C", text: "Gross Margin, Operating Profit, EBITDA, and Net Income" },
          { key: "D", text: "Debt, Equity, Working Capital, and Capex" }
        ],
        correctKey: "A",
        explanation: "The Balanced Scorecard pairs traditional financial lagging indicators with leading operational indicators across Customer relationships, Internal Processes, and Organizational Learning."
      },
      {
        id: "acc811-q3",
        question: "In corporate transfer pricing between operating subsidiaries, which international principle is mandated by the OECD and Nigerian tax authorities (FIRS) to curb profit shifting?",
        options: [
          { key: "A", text: "The Cost-Minus 50% rule" },
          { key: "B", text: "The Arm's Length Principle (transactions must reflect market prices between independent unrelated parties)" },
          { key: "C", text: "Arbitrary executive discretion" },
          { key: "D", text: "Tax haven zero-margin routing" }
        ],
        correctKey: "B",
        explanation: "The Arm's Length Principle ensures inter-company transfer prices match prices that would have been agreed upon by independent commercial enterprises under comparable market conditions."
      }
    ],
    flashcards: [
      {
        term: "Activity-Based Costing (ABC)",
        question: "How does ABC trace corporate overhead?",
        definition: "A two-stage costing system: overhead is first assigned to cost pools based on organizational activities, and then allocated to cost objects via specific cost drivers (e.g., setup hours).",
        context: "Eliminates cross-subsidization of unprofitable low-volume corporate client accounts."
      },
      {
        term: "Balanced Scorecard",
        question: "What is the strategic rationale of the Balanced Scorecard?",
        definition: "A performance management framework translating mission and strategy into measurable objectives across Financial, Customer, Internal Business Processes, and Learning & Growth.",
        context: "Prevents executive decisions that maximize quarterly earnings by gutting R&D and training."
      },
      {
        term: "Arm's Length Principle",
        question: "What is the Arm's Length Principle in transfer pricing?",
        definition: "The statutory benchmark requiring transactions between associated corporate entities to be priced on terms identical to transactions between independent third parties.",
        context: "Strictly audited by the Federal Inland Revenue Service (FIRS) to prevent cross-border base erosion."
      },
      {
        term: "Target Costing",
        question: "What is the formula and philosophy of Target Costing?",
        definition: "$\\text{Target Cost} = \\text{Target Competitive Price} - \\text{Required Margin}$. Cost is viewed as an input to product engineering rather than an outcome of manufacturing.",
        context: "Pioneered in executive manufacturing and high-technology consumer product launches."
      }
    ]
  },

  // =======================================================================================
  // 10. MAF 811: Research Methodology (Dr Udeme Eshiet)
  // =======================================================================================
  "MAF 811": {
    quizTitle: "MAF 811: Accounting & Finance Research Methodology Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating thesis research designs, sampling frameworks, and instrument validity.",
    flashcardTitle: "MAF 811 Research Methodology Flashcards",
    flashcardSubtitle: "Methodological rigor for master's and doctoral empirical accounting and finance dissertations.",
    questions: [
      {
        id: "maf811-q1",
        question: "In formulating an empirical Master's thesis in accounting and finance, what defines a valid testable Research Hypothesis ($H_1$)?",
        options: [
          { key: "A", text: "It must be an ambiguous rhetorical question that defies statistical verification" },
          { key: "B", text: "It states an explicit, directional or non-directional relationship between operationalized variables that can be verified or falsified with empirical data" },
          { key: "C", text: "It is an unchangeable institutional dogma that guarantees the null hypothesis is rejected" },
          { key: "D", text: "It substitutes for a literature review" }
        ],
        correctKey: "B",
        explanation: "Hypotheses must be empirically falsifiable statements derived from theoretical literature, specifying relationships between independent and dependent variables."
      },
      {
        id: "maf811-q2",
        question: "When conducting empirical research across Nigerian quoted companies categorized into Banking, Manufacturing, and Oil & Gas sectors, which sampling technique guarantees representative sub-group inclusion?",
        options: [
          { key: "A", text: "Haphazard convenience intercept sampling" },
          { key: "B", text: "Stratified Random Sampling (proportional or disproportional)" },
          { key: "C", text: "Snowball sampling among close social contacts" },
          { key: "D", text: "Voluntary self-selection internet poll" }
        ],
        correctKey: "B",
        explanation: "Stratified Random Sampling divides the population into mutually exclusive strata (sectors), ensuring every sub-sector has proportional representation without sampling bias."
      },
      {
        id: "maf811-q3",
        question: "In survey-based accounting research evaluating managerial perceptions, what metric measures the internal consistency reliability of multi-item Likert scales?",
        options: [
          { key: "A", text: "Gross Domestic Product deflator" },
          { key: "B", text: "Cronbach's Alpha (benchmark >= 0.70)" },
          { key: "C", text: "Durbin-Watson statistic" },
          { key: "D", text: "Herfindahl-Hirschman Index" }
        ],
        correctKey: "B",
        explanation: "Cronbach's Alpha evaluates whether items within a latent construct measure the same underlying concept. Values above 0.70 indicate acceptable internal consistency."
      }
    ],
    flashcards: [
      {
        term: "Stratified Random Sampling",
        question: "Why use Stratified Sampling in corporate finance research?",
        definition: "The target population is stratified by key corporate attributes (e.g., market capitalization, industry sector), and random samples are drawn proportionally from each stratum.",
        context: "Prevents large conglomerates from completely crowding out mid-tier listed firms in sample selection."
      },
      {
        term: "Cronbach's Alpha ($\\alpha$)",
        question: "What is Cronbach's Alpha in survey measurement?",
        definition: "A coefficient of reliability evaluating how closely related a set of survey test items are as a group. Values $\\ge 0.70$ are standard for postgraduate survey instruments.",
        context: "Mandatory metric in dissertation chapters validating primary research questionnaires."
      },
      {
        term: "Type I vs Type II Error",
        question: "What is the difference between $\\alpha$ and $\\beta$ errors?",
        definition: "Type I error ($\\alpha$) occurs when a true null hypothesis is incorrectly rejected (false positive). Type II error ($\\beta$) occurs when a false null hypothesis fails to be rejected (false negative).",
        context: "Postgraduate empirical testing sets $\\alpha = 0.05$ to minimize Type I false discovery risk."
      },
      {
        term: "Construct Validity",
        question: "How is Construct Validity established in financial research?",
        definition: "Through Convergent Validity (items measuring the construct correlate strongly) and Discriminant Validity (the construct is empirically distinct from other constructs).",
        context: "Demonstrated via Fornell-Larcker criteria and Heterotrait-Monotrait (HTMT) ratios."
      }
    ]
  },

  // =======================================================================================
  // 11. DAF 901: Philosophy & Foundations of Business Research (Prof. Nkanikpo Ibok)
  // =======================================================================================
  "DAF 901": {
    quizTitle: "DAF 901: Philosophy & Foundations of Business Research Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating doctoral epistemology, ontology, axiology, and Saunders' Research Onion.",
    flashcardTitle: "DAF 901 Epistemological Foundations Flashcards",
    flashcardSubtitle: "Doctoral paradigms, philosophical stances, and methodological coherence.",
    questions: [
      {
        id: "daf901-q1",
        question: "An executive doctoral researcher seeking to measure the exact quantitative impact of board gender diversity on quarterly ROI across 100 quoted firms is operating primarily under which philosophical stance?",
        options: [
          { key: "A", text: "Social Constructionism" },
          { key: "B", text: "Positivism" },
          { key: "C", text: "Postmodern Interpretivism" },
          { key: "D", text: "Hermeneutic Phenomenology" }
        ],
        correctKey: "B",
        explanation: "Positivism assumes social reality exists objectively and independently of the researcher, emphasizing observable, quantifiable phenomena and law-like generalizations through hypothesis testing."
      },
      {
        id: "daf901-q2",
        question: "Under Saunders' Research Onion, if an inquiry begins with specific observed organizational events, identifies patterns, and progresses to formulating a conceptual framework, the research approach is:",
        options: [
          { key: "A", text: "Purely Deductive" },
          { key: "B", text: "Inductive" },
          { key: "C", text: "Deterministic" },
          { key: "D", text: "Axiological" }
        ],
        correctKey: "B",
        explanation: "Inductive reasoning moves from specific operational observations to broader generalizations and novel theoretical frameworks, in contrast to deductive hypothesis-testing."
      },
      {
        id: "daf901-q3",
        question: "Why is Critical Realism considered uniquely suited for doctoral inquiries into corporate governance and financial failure?",
        options: [
          { key: "A", text: "It denies the objective existence of corporate financial numbers" },
          { key: "B", text: "It stratifies reality into the Empirical, Actual, and Real, exploring underlying generative causal mechanisms behind boardroom collapse" },
          { key: "C", text: "It strictly forbids the use of quantitative statistical modeling" },
          { key: "D", text: "It eliminates the need for doctoral supervisory oversight" }
        ],
        correctKey: "B",
        explanation: "Critical Realism recognizes an objective social reality but argues that events (the Actual) are produced by deeper generative mechanisms (the Real) that must be critically theorized."
      }
    ],
    flashcards: [
      {
        term: "Ontology (Realism vs Nominalism)",
        question: "What is Ontology in Executive Doctoral Research?",
        definition: "The branch of philosophy concerning the nature of social reality. It asks: Does corporate reality exist independently of human consciousness (Realism), or is it constructed through managerial discourse (Nominalism)?",
        context: "Determines whether financial performance can be objectively measured or socially interpreted."
      },
      {
        term: "Epistemology",
        question: "What constitutes valid Epistemological grounding?",
        definition: "The branch of philosophy studying the nature, validity, and scope of knowledge. It asks: What constitutes legitimate, authoritative knowledge in business and how can researchers validate it?",
        context: "Distinguishes between Positivist hard data metrics and Interpretivist qualitative meanings."
      },
      {
        term: "Saunders' Research Onion",
        question: "What are the 6 concentric layers of Saunders' Onion?",
        definition: "1. Philosophy, 2. Approach, 3. Methodological Choice, 4. Strategy, 5. Time Horizon, 6. Techniques & Procedures.",
        context: "Mandatory doctoral scaffolding preventing methodological fragmentation in AKSU theses."
      },
      {
        term: "Critical Realism (Bhaskar)",
        question: "What are the three stratified domains of Critical Realism?",
        definition: "1. The Empirical (observable experiences), 2. The Actual (events occurring whether observed or not), 3. The Real (generative causal structures and mechanisms that trigger events).",
        context: "A profound paradigm for examining boardroom malfeasance and institutional banking fragility."
      }
    ]
  },

  // =======================================================================================
  // 12. ACC 911: Fin.Accounting Theory & research (Prof. Joseph Udoayang)
  // =======================================================================================
  "ACC 911": {
    quizTitle: "ACC 911: Financial Accounting Theory & Research Knowledge Check",
    quizSubtitle: "Continuous Assessment · Validating Positive Accounting Theory, Modified Jones Accruals, and Ohlson valuation models.",
    flashcardTitle: "ACC 911 Accounting Theory Flashcards",
    flashcardSubtitle: "Doctoral paradigms in positive accounting theory, earnings management, and value relevance.",
    questions: [
      {
        id: "acc911-q1",
        question: "Under Watts and Zimmerman's Positive Accounting Theory (PAT), which three core hypotheses explain opportunistic managerial accounting policy choices?",
        options: [
          { key: "A", text: "The Asset, Liability, and Equity hypotheses" },
          { key: "B", text: "The Bonus Plan hypothesis, the Debt Covenant hypothesis, and the Political Cost hypothesis" },
          { key: "C", text: "The Fair Value, Historical Cost, and Realization hypotheses" },
          { key: "D", text: "The Audit, Assurance, and Compliance hypotheses" }
        ],
        correctKey: "B",
        explanation: "PAT posits that managers choose accounting policies that maximize their personal utility: increasing bonuses (Bonus Plan), avoiding debt violations (Debt Covenant), and reducing political scrutiny (Political Cost)."
      },
      {
        id: "acc911-q2",
        question: "In empirical accounting research, which econometric model is the academic benchmark for isolating discretionary (manipulated) accruals from non-discretionary (operational) accruals?",
        options: [
          { key: "A", text: "The Modified Jones Model (Dechow, Sloan & Sweeney, 1995)" },
          { key: "B", text: "Simple three-month rolling moving averages" },
          { key: "C", text: "Cobb-Douglas macroeconomic production function" },
          { key: "D", text: "DuPont financial ratio decomposition" }
        ],
        correctKey: "A",
        explanation: "The Modified Jones Model estimates non-discretionary accruals based on revenue growth adjusted for credit sales and PPE depreciation; the residual error term represents discretionary accruals."
      },
      {
        id: "acc911-q3",
        question: "In the Ohlson (1995) Clean Surplus Equity Valuation framework, how is the intrinsic market value of a firm modeled?",
        options: [
          { key: "A", text: "Book value of equity plus the discounted present value of expected future abnormal earnings (residual income)" },
          { key: "B", text: "Strict historical book value of physical factory assets" },
          { key: "C", text: "Gross cash sales divided by the risk-free rate" },
          { key: "D", text: "Sum of executive payroll and statutory corporate taxes" }
        ],
        correctKey: "A",
        explanation: "Ohlson's model formalizes value relevance research by demonstrating that firm value equals current book equity plus the infinite discounted stream of future residual earnings exceeding the cost of capital."
      }
    ],
    flashcards: [
      {
        term: "Positive Accounting Theory (PAT)",
        question: "What is the foundational premise of PAT?",
        definition: "A research paradigm predicting and explaining why corporate managers select specific accounting methods based on self-interest, executive contracts, and debt convenants rather than normative ideals.",
        context: "The core theoretical underpinning of empirical doctoral financial accounting research."
      },
      {
        term: "Modified Jones Model (1995)",
        question: "How does the Modified Jones Model detect earnings management?",
        definition: "Regresses total accruals against inverse assets, revenue change minus receivables change, and gross PPE. The regression residual captures managerial discretionary accrual manipulation.",
        context: "The universal empirical proxy for earnings quality in doctoral accounting theses."
      },
      {
        term: "Ohlson (1995) Clean Surplus Model",
        question: "What does the Ohlson Valuation Model achieve?",
        definition: "Directly bridges accounting metrics (book value of equity and net income) to capital market equity valuation through the clean surplus accounting relation ($B_t = B_{t-1} + NI_t - Div_t$).",
        context: "The premier framework for testing the value relevance of IFRS and ESG disclosures."
      },
      {
        term: "Agency Theory in Auditing",
        question: "Why do shareholders demand high-quality external audits?",
        definition: "Audited financial reports reduce information asymmetry between principals (shareholders) and agents (managers), curtailing perquisite consumption and lowering borrowing costs.",
        context: "Underpins Big-4 audit quality research and auditor independence inquiries."
      }
    ]
  }
};

// Aliases for shared course assignments
COURSE_ASSESSMENTS["DAF 911"] = COURSE_ASSESSMENTS["BUS 911"]; // Econometrics
COURSE_ASSESSMENTS["FIN 911"] = COURSE_ASSESSMENTS["FIN 811"]; // Valuation & Asset Pricing
COURSE_ASSESSMENTS["DAF 941"] = COURSE_ASSESSMENTS["BUS 941"]; // Research Writing & Ethics

// Retrieval Helpers
export const getCourseQuiz = (courseCode) => {
  if (!courseCode) return COURSE_ASSESSMENTS["BUS 901"].questions;
  const cleanCode = courseCode.toUpperCase().trim();
  const match = COURSE_ASSESSMENTS[cleanCode];
  if (match && match.questions) {
    return match.questions;
  }
  // Try partial match
  const key = Object.keys(COURSE_ASSESSMENTS).find(k => cleanCode.includes(k) || k.includes(cleanCode));
  if (key && COURSE_ASSESSMENTS[key].questions) {
    return COURSE_ASSESSMENTS[key].questions;
  }
  return COURSE_ASSESSMENTS["BUS 901"].questions;
};

export const getCourseFlashcards = (courseCode) => {
  if (!courseCode) return COURSE_ASSESSMENTS["BUS 901"].flashcards;
  const cleanCode = courseCode.toUpperCase().trim();
  const match = COURSE_ASSESSMENTS[cleanCode];
  if (match && match.flashcards) {
    return match.flashcards;
  }
  // Try partial match
  const key = Object.keys(COURSE_ASSESSMENTS).find(k => cleanCode.includes(k) || k.includes(cleanCode));
  if (key && COURSE_ASSESSMENTS[key].flashcards) {
    return COURSE_ASSESSMENTS[key].flashcards;
  }
  return COURSE_ASSESSMENTS["BUS 901"].flashcards;
};

export const getCourseAssessmentMeta = (courseCode) => {
  if (!courseCode) return COURSE_ASSESSMENTS["BUS 901"];
  const cleanCode = courseCode.toUpperCase().trim();
  const match = COURSE_ASSESSMENTS[cleanCode];
  if (match) return match;
  const key = Object.keys(COURSE_ASSESSMENTS).find(k => cleanCode.includes(k) || k.includes(cleanCode));
  if (key) return COURSE_ASSESSMENTS[key];
  return COURSE_ASSESSMENTS["BUS 901"];
};
